/**
 * Lighting Manager
 * 
 * Manages scene lighting configuration including ambient, directional,
 * point, and spot lights with shadow support.
 */

import * as THREE from 'three';
import type { 
  LightingConfig, 
  AmbientLightConfig,
  DirectionalLightConfig,
  PointLightConfig,
  SpotLightConfig,
  IManager 
} from '../types';

export interface LightingManagerConfig {
  lighting?: LightingConfig;
}

interface LightEntry {
  light: THREE.Light;
  helper?: THREE.Object3D;
}

export class LightingManager implements IManager {
  private scene: THREE.Scene | null = null;
  private lights: Map<string, LightEntry> = new Map();
  private showHelpers = false;
  
  /**
   * Initialize lighting from config
   */
  public async init(scene: THREE.Scene, config: LightingManagerConfig): Promise<void> {
    this.scene = scene;
    
    // Clear existing lights
    this.clearLights();
    
    const lightingConfig = config.lighting;
    
    if (!lightingConfig) {
      // Add default lighting if no config
      this.addDefaultLighting();
      return;
    }
    
    // Add ambient light
    if (lightingConfig.ambient) {
      this.addAmbientLight(lightingConfig.ambient);
    }
    
    // Add directional lights
    if (lightingConfig.directional) {
      lightingConfig.directional.forEach((config, index) => {
        this.addDirectionalLight(config, `directional_${index}`);
      });
    }
    
    // Add point lights
    if (lightingConfig.points) {
      lightingConfig.points.forEach((config, index) => {
        this.addPointLight(config, `point_${index}`);
      });
    }
    
    // Add spot lights
    if (lightingConfig.spots) {
      lightingConfig.spots.forEach((config, index) => {
        this.addSpotLight(config, `spot_${index}`);
      });
    }
  }
  
  /**
   * Add default lighting setup
   */
  private addDefaultLighting(): void {
    // Ambient
    this.addAmbientLight({ color: '#ffffff', intensity: 0.4 });
    
    // Key light
    this.addDirectionalLight({
      color: '#ffffff',
      intensity: 0.8,
      position: [5, 5, 5],
      castShadow: true,
      shadowMapSize: 1024,
    }, 'key');
    
    // Fill light
    this.addDirectionalLight({
      color: '#ffffff',
      intensity: 0.3,
      position: [-5, 3, -5],
      castShadow: false,
    }, 'fill');
  }
  
  /**
   * Add ambient light
   */
  public addAmbientLight(config: AmbientLightConfig, id = 'ambient'): THREE.AmbientLight {
    const light = new THREE.AmbientLight(
      config.color ?? '#ffffff',
      config.intensity ?? 0.5
    );
    
    this.scene?.add(light);
    this.lights.set(id, { light });
    
    return light;
  }
  
  /**
   * Add directional light
   */
  public addDirectionalLight(config: DirectionalLightConfig, id: string): THREE.DirectionalLight {
    const light = new THREE.DirectionalLight(
      config.color ?? '#ffffff',
      config.intensity ?? 1
    );
    
    light.position.set(...config.position);
    
    if (config.castShadow) {
      light.castShadow = true;
      light.shadow.mapSize.width = config.shadowMapSize ?? 1024;
      light.shadow.mapSize.height = config.shadowMapSize ?? 1024;
      light.shadow.camera.near = 0.5;
      light.shadow.camera.far = 50;
      light.shadow.camera.left = -10;
      light.shadow.camera.right = 10;
      light.shadow.camera.top = 10;
      light.shadow.camera.bottom = -10;
    }
    
    this.scene?.add(light);
    
    // Add helper if enabled
    let helper: THREE.DirectionalLightHelper | undefined;
    if (this.showHelpers) {
      helper = new THREE.DirectionalLightHelper(light, 1);
      this.scene?.add(helper);
    }
    
    this.lights.set(id, { light, helper });
    
    return light;
  }
  
  /**
   * Add point light
   */
  public addPointLight(config: PointLightConfig, id: string): THREE.PointLight {
    const light = new THREE.PointLight(
      config.color ?? '#ffffff',
      config.intensity ?? 1,
      config.distance ?? 0,
      config.decay ?? 2
    );
    
    light.position.set(...config.position);
    
    if (config.castShadow) {
      light.castShadow = true;
      light.shadow.mapSize.width = 512;
      light.shadow.mapSize.height = 512;
    }
    
    this.scene?.add(light);
    
    // Add helper if enabled
    let helper: THREE.PointLightHelper | undefined;
    if (this.showHelpers) {
      helper = new THREE.PointLightHelper(light, 0.2);
      this.scene?.add(helper);
    }
    
    this.lights.set(id, { light, helper });
    
    return light;
  }
  
  /**
   * Add spot light
   */
  public addSpotLight(config: SpotLightConfig, id: string): THREE.SpotLight {
    const light = new THREE.SpotLight(
      config.color ?? '#ffffff',
      config.intensity ?? 1,
      config.distance ?? 0,
      config.angle ?? Math.PI / 6,
      config.penumbra ?? 0.1,
      config.decay ?? 2
    );
    
    light.position.set(...config.position);
    
    if (config.target) {
      light.target.position.set(...config.target);
      this.scene?.add(light.target);
    }
    
    if (config.castShadow) {
      light.castShadow = true;
      light.shadow.mapSize.width = 1024;
      light.shadow.mapSize.height = 1024;
    }
    
    this.scene?.add(light);
    
    // Add helper if enabled
    let helper: THREE.SpotLightHelper | undefined;
    if (this.showHelpers) {
      helper = new THREE.SpotLightHelper(light);
      this.scene?.add(helper);
    }
    
    this.lights.set(id, { light, helper });
    
    return light;
  }
  
  /**
   * Update a light's properties
   */
  public updateLight(
    id: string, 
    properties: Partial<{
      color: string;
      intensity: number;
      position: [number, number, number];
    }>
  ): void {
    const entry = this.lights.get(id);
    if (!entry) return;
    
    const light = entry.light;
    
    if (properties.color && 'color' in light) {
      (light as THREE.DirectionalLight).color.set(properties.color);
    }
    
    if (properties.intensity !== undefined) {
      light.intensity = properties.intensity;
    }
    
    if (properties.position) {
      light.position.set(...properties.position);
    }
    
    // Update helper
    if (entry.helper && 'update' in entry.helper) {
      (entry.helper as THREE.DirectionalLightHelper).update();
    }
  }
  
  /**
   * Remove a light
   */
  public removeLight(id: string): void {
    const entry = this.lights.get(id);
    if (!entry) return;
    
    this.scene?.remove(entry.light);
    if (entry.helper) {
      this.scene?.remove(entry.helper);
    }
    
    entry.light.dispose?.();
    this.lights.delete(id);
  }
  
  /**
   * Toggle light helpers visibility
   */
  public setShowHelpers(show: boolean): void {
    this.showHelpers = show;
    
    // Update existing lights
    this.lights.forEach((entry, _id) => {
      if (show && !entry.helper) {
        // Add helper
        const light = entry.light;
        let helper: THREE.Object3D | undefined;
        
        if (light instanceof THREE.DirectionalLight) {
          helper = new THREE.DirectionalLightHelper(light, 1);
        } else if (light instanceof THREE.PointLight) {
          helper = new THREE.PointLightHelper(light, 0.2);
        } else if (light instanceof THREE.SpotLight) {
          helper = new THREE.SpotLightHelper(light);
        }
        
        if (helper) {
          this.scene?.add(helper);
          entry.helper = helper;
        }
      } else if (!show && entry.helper) {
        // Remove helper
        this.scene?.remove(entry.helper);
        entry.helper = undefined;
      }
    });
  }
  
  /**
   * Clear all lights
   */
  private clearLights(): void {
    this.lights.forEach((entry) => {
      this.scene?.remove(entry.light);
      if (entry.helper) {
        this.scene?.remove(entry.helper);
      }
      entry.light.dispose?.();
    });
    this.lights.clear();
  }
  
  /**
   * Get current lighting state
   */
  public getState(): LightingConfig {
    const config: LightingConfig = {
      directional: [],
      points: [],
      spots: [],
    };
    
    this.lights.forEach((entry, _id) => {
      const light = entry.light;
      
      if (light instanceof THREE.AmbientLight) {
        config.ambient = {
          color: '#' + light.color.getHexString(),
          intensity: light.intensity,
        };
      } else if (light instanceof THREE.DirectionalLight) {
        config.directional?.push({
          color: '#' + light.color.getHexString(),
          intensity: light.intensity,
          position: light.position.toArray() as [number, number, number],
          castShadow: light.castShadow,
        });
      } else if (light instanceof THREE.PointLight) {
        config.points?.push({
          color: '#' + light.color.getHexString(),
          intensity: light.intensity,
          position: light.position.toArray() as [number, number, number],
          distance: light.distance,
          decay: light.decay,
          castShadow: light.castShadow,
        });
      } else if (light instanceof THREE.SpotLight) {
        config.spots?.push({
          color: '#' + light.color.getHexString(),
          intensity: light.intensity,
          position: light.position.toArray() as [number, number, number],
          angle: light.angle,
          penumbra: light.penumbra,
          distance: light.distance,
          decay: light.decay,
          castShadow: light.castShadow,
        });
      }
    });
    
    return config;
  }
  
  /**
   * Update (called every frame)
   */
  public update(_deltaTime: number): void {
    // Update spot light helpers
    this.lights.forEach((entry) => {
      if (entry.helper instanceof THREE.SpotLightHelper) {
        entry.helper.update();
      }
    });
  }
  
  /**
   * Dispose resources
   */
  public dispose(): void {
    this.clearLights();
    this.scene = null;
  }
}
